<template>
  <div id="app">
    <header class="hb-topbar px-3">
      <div class="navbar-left">
        <button @click="toggleSidebar()" class="header-icon-btn" aria-label="Alternar menú lateral">
          <i class="fas fa-bars"></i>
        </button>
      </div>
      <div class="ms-auto position-relative navbar-right">
        <!-- Orden pedido: campana · luna · nombre (el nombre queda al borde derecho) -->
        <button @click="toggleDropdownNotifications()" class="header-icon-btn position-relative" aria-label="Ver alertas">
          <i class="fas fa-bell"></i>
          <span v-if="unreadCount > 0" class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {{ unreadCount }}
          </span>
        </button>
        <button @click="uiStore.toggleTheme()" class="header-icon-btn" :aria-label="uiStore.esOscuro ? 'Activar modo claro' : 'Activar modo oscuro'">
          <i :class="uiStore.esOscuro ? 'fas fa-sun' : 'fas fa-moon'"></i>
        </button>
        <!-- Proyecto activo: el propio nombre es el botón desplegable -->
        <div v-if="proyectName" class="account-menu-wrap">
          <button
            type="button"
            class="navbar-brand navbar-brand--header account-menu-btn"
            :class="{ active: showDropdownCuenta }"
            :aria-expanded="showDropdownCuenta ? 'true' : 'false'"
            aria-haspopup="menu"
            :aria-label="`Proyecto ${proyectName}: abrir menú`"
            @click.stop="toggleDropdownCuenta()"
          >
            <span class="account-menu-label">{{ proyectName }}</span>
            <i class="fas fa-chevron-down account-menu-caret" :class="{ 'rotate-180': showDropdownCuenta }" aria-hidden="true"></i>
          </button>
          <div v-show="showDropdownCuenta" class="account-menu" role="menu" aria-label="Opciones de sesión">
            <!--
              Solo opciones de sesión/cuenta. Para agregar futuras opciones
              basta replicar un bloque con la clase `account-menu-item`
              (router-link o button) y separarlas con `account-menu-sep`:

              <router-link class="account-menu-item" to="/perfil" role="menuitem" @click="showDropdownCuenta = false">
                <i class="fas fa-user-pen"></i><span>Editar Perfil</span>
              </router-link>
              <div class="account-menu-sep"></div>
            -->
            <router-link class="account-menu-item" to="/mi-cuenta" role="menuitem" @click="showDropdownCuenta = false">
                    <i class="fas fa-user-shield"></i><span>Mi cuenta</span>
                  </router-link>
                  <div class="account-menu-sep"></div>
                  <button type="button" class="account-menu-item account-menu-item--danger" role="menuitem" @click="logout()">
              <i class="fas fa-power-off"></i>
              <span>Salir</span>
            </button>
          </div>
        </div>

        <div v-show="showDropdownNotifications" class="notif-menu" role="menu" aria-label="Alertas">
          <div class="notif-header">
            <span class="notif-title">Alertas</span>
            <button
              type="button"
              class="notif-mark-all"
              :disabled="unreadCount === 0"
              @click.stop="markAllRead"
            >
              Marcar todas como leídas
            </button>
          </div>
          <div class="notif-body">
            <div v-if="notifications.alertasSistema.length === 0" class="notif-empty">
              <i class="far fa-bell-slash"></i>
              <span>Sin alertas</span>
            </div>
            <div
              v-for="alerta in alertasVisibles"
              :key="alerta.idAlerts || alerta.id || alerta.uuid"
              class="notif-item"
              :class="{ unread: !alerta.isRead }"
            >
              <span class="notif-dot" :class="{ on: !alerta.isRead }" aria-hidden="true"></span>
              <div class="notif-content">
                <div class="notif-row-top">
                  <span class="notif-station">{{ formatAlertStation(alerta) }}</span>
                  <span class="notif-time">{{ formatAlertTime(alerta) }}</span>
                </div>
                <!-- Texto plano a propósito: el mensaje viene del servidor y v-html permitiría XSS -->
                <div class="notif-text">{{ formatAlertMessage(alerta) }}</div>
              </div>
              <button
                v-if="!alerta.isRead"
                type="button"
                class="notif-read-btn"
                title="Marcar como leída"
                @click.stop="markOneRead(alerta)"
              >
                <i class="fas fa-check"></i>
              </button>
            </div>
          </div>
          <router-link
            v-if="notifications.alertasSistema.length > 0"
            to="/log_alertas"
            class="notif-footer"
            @click="showDropdownNotifications = false"
          >
            <span v-if="alertasOcultas > 0">Ver {{ alertasOcultas }} alerta{{ alertasOcultas === 1 ? '' : 's' }} más en el log</span>
            <span v-else>Ir al log de alertas</span>
            <i class="fas fa-arrow-right"></i>
          </router-link>
        </div>
      </div>
    </header>

    <div class="admin-layout">
      <button
        v-if="isMobileView && !isCollapsed"
        type="button"
        class="sidebar-backdrop"
        aria-label="Cerrar menú lateral"
        @click="closeSidebarOnMobile"
      ></button>
      <nav :class="['sidebar', { collapsed: isCollapsed }, 'd-flex', 'flex-column']">
        <!-- Dropdown de proyectos si hay múltiples -->
        <div v-if="proyectos.length > 1" class="nav-item dropdown" :class="{ show: showDropdownProyectos }">
          <a href="javascript:void(0)" class="nav-link" @click.prevent="toggleDropdownProyectos">
            <i class="fas fa-folder-tree"></i>
            <span v-if="!isCollapsed">Proyectos</span>
            <i class="fas fa-chevron-down ms-auto" style="font-size: 10px;" :class="{ 'rotate-180': showDropdownProyectos }" v-if="!isCollapsed"></i>
          </a>
          <div class="dropdown-menu" :class="{ show: showDropdownProyectos }" v-show="showDropdownProyectos">
            <a v-for="(proyecto, i) in proyectos" :key="i" class="dropdown-item" href=""
              @click.prevent="seleccionarProyecto(i)">
              <i class="fas fa-circle me-2" style="font-size: 5px;"></i> {{ proyecto.proyectoName }}
            </a>
          </div>
        </div>

        <!-- Dashboard -->
        <router-link class="nav-link" to="/dashboard">
          <i class="fas fa-gauge-high"></i>
          <span v-if="!isCollapsed">Dashboard</span>
        </router-link>

        <!-- Home/Mapa -->
        <router-link class="nav-link" to="/home">
          <i class="fas fa-map"></i>
          <span v-if="!isCollapsed">Mapa</span>
        </router-link>

        <!-- Cuarteles -->
        <div v-if="cuarteles.length > 0" class="nav-item dropdown" :class="{ show: showDropdownCuarteles }">
          <a href="javascript:void(0)" class="nav-link" @click.prevent="showDropdownCuarteles = !showDropdownCuarteles">
            <i class="fas fa-layer-group"></i>
            <span v-if="!isCollapsed">Cuarteles</span>
            <i class="fas fa-chevron-down ms-auto" style="font-size: 10px;" :class="{ 'rotate-180': showDropdownCuarteles }" v-if="!isCollapsed"></i>
          </a>
          <div class="dropdown-menu" :class="{ show: showDropdownCuarteles }" v-show="showDropdownCuarteles">
            <router-link v-for="cuartel in cuarteles" :key="cuartel.id" class="dropdown-item" :to="cuartel.url" @click="closeModuleDropdowns">
              <i class="fas fa-circle me-2" style="font-size: 5px;"></i> {{ cuartel.nombre }}
            </router-link>
          </div>
        </div>

        <!-- Casetas -->
        <div v-if="casetas.length > 0" class="nav-item dropdown" :class="{ show: showDropdownCasetas }">
          <a href="javascript:void(0)" class="nav-link" @click.prevent="showDropdownCasetas = !showDropdownCasetas">
            <i class="fas fa-house"></i>
            <span v-if="!isCollapsed">Casetas</span>
            <i class="fas fa-chevron-down ms-auto" style="font-size: 10px;" :class="{ 'rotate-180': showDropdownCasetas }" v-if="!isCollapsed"></i>
          </a>
          <div class="dropdown-menu" :class="{ show: showDropdownCasetas }" v-show="showDropdownCasetas">
            <router-link v-for="caseta in casetas" :key="caseta.id" class="dropdown-item" :to="caseta.url" @click="closeModuleDropdowns">
              <i class="fas fa-circle me-2" style="font-size: 5px;"></i> {{ caseta.nombre }}
            </router-link>
          </div>
        </div>

        <!-- Meteorológicas -->
        <div v-if="meteorologicas.length > 0" class="nav-item dropdown" :class="{ show: showDropdownMeteo }">
          <a href="javascript:void(0)" class="nav-link" @click.prevent="showDropdownMeteo = !showDropdownMeteo">
            <i class="fas fa-cloud-sun"></i>
            <span v-if="!isCollapsed">Meteorológicas</span>
            <i class="fas fa-chevron-down ms-auto" style="font-size: 10px;" :class="{ 'rotate-180': showDropdownMeteo }" v-if="!isCollapsed"></i>
          </a>
          <div class="dropdown-menu" :class="{ show: showDropdownMeteo }" v-show="showDropdownMeteo">
            <router-link v-for="meteo in meteorologicas" :key="meteo.id" class="dropdown-item" :to="meteo.url" @click="closeModuleDropdowns">
              <i class="fas fa-circle me-2" style="font-size: 5px;"></i> {{ meteo.nombre }}
            </router-link>
          </div>
        </div>

        <!-- Alertas -->
        <router-link class="nav-link" to="/alertas">
          <i class="fas fa-bell"></i>
          <span v-if="!isCollapsed">Alertas</span>
        </router-link>

        <!-- Administración -->
        <div v-if="administracion.length > 0" class="nav-item dropdown" :class="{ show: showDropdownAdmin }">
          <a href="javascript:void(0)" class="nav-link" @click.prevent="showDropdownAdmin = !showDropdownAdmin">
            <i class="fas fa-screwdriver-wrench"></i>
            <span v-if="!isCollapsed">Administración</span>
            <i class="fas fa-chevron-down ms-auto" style="font-size: 10px;" :class="{ 'rotate-180': showDropdownAdmin }" v-if="!isCollapsed"></i>
          </a>
          <div class="dropdown-menu" :class="{ show: showDropdownAdmin }" v-show="showDropdownAdmin">
            <router-link v-for="admin in administracion" :key="admin.id" class="dropdown-item" :to="admin.url" @click="closeModuleDropdowns">
              <i class="fas fa-circle me-2" style="font-size: 5px;"></i> {{ admin.nombre }}
            </router-link>
            <router-link v-if="authStore.esSuperAdmin" class="dropdown-item" to="/asistente" @click="closeModuleDropdowns">
              <i class="fas fa-circle me-2" style="font-size: 5px;"></i> Asistente de Configuración
            </router-link>
            <router-link v-if="authStore.esSuperAdmin" class="dropdown-item" to="/organizaciones" @click="closeModuleDropdowns">
              <i class="fas fa-circle me-2" style="font-size: 5px;"></i> Organizaciones
            </router-link>
            <router-link v-if="authStore.esSuperAdmin" class="dropdown-item" to="/cambiar-clave" @click="closeModuleDropdowns">
              <i class="fas fa-circle me-2" style="font-size: 5px;"></i> Cambiar Clave
            </router-link>
            <router-link v-if="authStore.esSuperAdmin" class="dropdown-item" to="/importar-datos" @click="closeModuleDropdowns">
              <i class="fas fa-circle me-2" style="font-size: 5px;"></i> Importar datos
            </router-link>
          </div>
        </div>

        <!-- Separador -->
        <div style="flex: 1;"></div>

        <!-- "Salir" vive ahora en el menú de usuario de la barra superior -->
        <div v-if="!isCollapsed" class="sidebar-footer mt-auto">
          <div class="sidebar-footer__brand">
            &copy; {{ currentYear }} Creado por Hidrobytes
          </div>
          <div class="sidebar-footer__version">
            v{{ appVersion }}
          </div>
        </div>
      </nav>

      <main :class="['content', { 'content--map': $route?.path === '/home' }]">
        <slot></slot>
      </main>
    </div>
  </div>
</template>

<script>
import { fetchAlertsCustom, prepareAlertsData, fetchWithToken, safeJsonParse } from '../utils/helpers';
import { getPerfilSession, getUserTypeFallbackFromToken, getUserTypeFromProyectos } from '../utils/auth';
import { useAuthStore } from '../stores/auth';
import { useProyectoStore } from '../stores/proyecto';
import { useUiStore } from '../stores/ui';
import { formatDate } from '../utils/formatProjectDate';
import { sinTipoDuplicado } from '../utils/entityLabel';
// El "dónde" y el "qué" de una alerta se arman en utils/alertas.js, compartido
// con la tarjeta "Alertas recientes" del Dashboard, para que la campana y el
// Dashboard nunca describan distinto la misma alerta.
import { formatAlertStation as alertaEstacion, formatAlertMessage as alertaMensaje } from '../utils/alertas';

export default {
  name: 'LayoutAdmin',
  // Estado de sesión/proyecto/tema centralizado en Pinia (ver src/stores/)
  setup() {
    const authStore = useAuthStore();
    const proyectoStore = useProyectoStore();
    const uiStore = useUiStore();
    return { authStore, proyectoStore, uiStore };
  },
  computed: {
    proyectos() { return this.proyectoStore.proyectos; },
    proyectName() { return this.proyectoStore.proyectoNombre; },
    proyectId() { return this.proyectoStore.proyectoId; },
    // La campanita muestra como maximo las 10 alertas mas recientes; el resto
    // se consultan en el log de alertas (link al pie del dropdown).
    alertasVisibles() {
      return (this.notifications?.alertasSistema || []).slice(0, this.MAX_ALERTAS_CAMPANITA);
    },
    alertasOcultas() {
      const total = (this.notifications?.alertasSistema || []).length;
      return Math.max(0, total - this.MAX_ALERTAS_CAMPANITA);
    }
  },
  data() {
    return {
      isCollapsed: false,
      showDropdownNotifications: false,
      showDropdownProyectos: false,
      // Menú de sesión colgado del nombre del proyecto en la barra superior
      showDropdownCuenta: false,
      unreadCount: 0,
      notifications: { cantidadAlertas: 0, alertasSistema: [] },
      markedReadThisSession: new Set(),
      isMarkingRead: false,
      MAX_ALERTAS_CAMPANITA: 10,
      // Módulos extraídos del menú
      cuarteles: [],
      casetas: [],
      meteorologicas: [],
      alertas: [],
      administracion: [],
      // Estados de dropdowns para módulos
      showDropdownCuarteles: false,
      showDropdownCasetas: false,
      showDropdownMeteo: false,
      showDropdownAlertas: false,
      showDropdownAdmin: false,
      currentYear: new Date().getFullYear(),
      // Se inyecta en el build desde package.json (ver vue.config.js). Estaba
      // escrita a mano y quedo cuatro versiones atras: el pie decia v2.15.1 con
      // el paquete en 2.19.0.
      appVersion: process.env.VUE_APP_VERSION || '',
      perfilSession: null,
      userType: null,
      isMobileView: false,
      resizeHandler: null,
      documentClickHandler: null,
      keydownHandler: null
    };
  },
  watch: {
    '$route.fullPath'() {
      this.closeModuleDropdowns();
      this.showDropdownProyectos = false;
      this.showDropdownNotifications = false;
      this.showDropdownCuenta = false;
      this.closeSidebarOnMobile();
    }
  },
  async mounted() {
    // Cargar sesión/proyecto desde el store (persistencia en storage)
    this.proyectoStore.hydrate();

    // Fallback: si no hay proyectos en storage, pedirlos al backend
    if (!this.proyectos.length) {
      const usuario = this.$root?.$data?.usuarioActual;
      if (usuario?.id) {
        try {
          const res = await fetchWithToken(`/api/general-access-user?id=${usuario.id}`, { method: 'GET' });
          if (res.ok) {
            const data = await res.json();
            const proyectos = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
            if (proyectos.length) {
              this.proyectoStore.setProyectos(proyectos);
            }
          }
        } catch (e) {
          console.warn('No se pudieron hidratar proyectos:', e);
        }
      }
    }

    this.perfilSession = getPerfilSession();
    this.userType = getUserTypeFromProyectos(this.proyectos, this.proyectId) ?? getUserTypeFallbackFromToken();

    // Cargar módulos del menú guardado
    const menuGuardado = sessionStorage.getItem('menu');
    if (menuGuardado) {
      try {
        const menu = safeJsonParse(menuGuardado, null);
        if (menu) {
          this.cargarModulos(menu);
        }
      } catch (e) {
        console.warn('Error parsing menu:', e);
      }
    }

    // Cargar notificaciones si existen
    const notif = sessionStorage.getItem('notifications');
    if (notif) {
      try {
        const data = safeJsonParse(notif, null);
        this.notifications = data || { cantidadAlertas: 0, alertasSistema: [] };
        this.recomputeUnread();
      } catch (e) {
        console.warn('Error parsing notifications:', e);
      }
    }

    this.refreshNotifications();

    this.resizeHandler = this.handleViewportResize;
    this.documentClickHandler = (e) => {
      const bell = document.querySelector('.fa-bell');
      const panel = document.querySelector('.notif-menu');
      if (panel && !panel.contains(e.target) && !bell?.contains(e.target)) {
        this.showDropdownNotifications = false;
      }

      const cuentaBtn = document.querySelector('.account-menu-btn');
      const cuentaPanel = document.querySelector('.account-menu');
      if (cuentaPanel && !cuentaPanel.contains(e.target) && !cuentaBtn?.contains(e.target)) {
        this.showDropdownCuenta = false;
      }
    };
    this.keydownHandler = (e) => {
      if (e.key === 'Escape') {
        this.showDropdownCuenta = false;
        this.showDropdownNotifications = false;
      }
    };

    // El menú se armaba SOLO aquí, en mounted(), leyendo sessionStorage.menu.
    // Cuando el menú cambia en caliente (submódulos habilitados en el Centro de
    // Accesos, un cuartel o caseta nuevos) nadie reconstruía el sidebar, así que
    // hacía falta cerrar sesión y volver a entrar. Ahora se reconstruye con el
    // mismo evento que ya emite el store (ver stores/proyecto.js).
    this.sessionUpdatedHandler = () => {
      this.proyectoStore.hydrate();
      this.userType = getUserTypeFromProyectos(this.proyectos, this.proyectId) ?? getUserTypeFallbackFromToken();
      const menu = safeJsonParse(sessionStorage.getItem('menu'), null);
      if (menu) this.cargarModulos(menu);
    };
    window.addEventListener('hb:session-updated', this.sessionUpdatedHandler);

    this.handleViewportResize();
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('click', this.documentClickHandler);
    window.addEventListener('keydown', this.keydownHandler);
  },
  beforeUnmount() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.documentClickHandler) {
      window.removeEventListener('click', this.documentClickHandler);
    }
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
    }
    if (this.sessionUpdatedHandler) {
      window.removeEventListener('hb:session-updated', this.sessionUpdatedHandler);
    }
  },
  methods: {
    handleViewportResize() {
      const isMobile = window.innerWidth <= 768;
      if (isMobile === this.isMobileView) {
        return;
      }

      this.isMobileView = isMobile;
      if (isMobile) {
        this.isCollapsed = true;
      } else {
        this.isCollapsed = false;
      }
    },
    closeSidebarOnMobile() {
      if (this.isMobileView) {
        this.isCollapsed = true;
        this.showDropdownProyectos = false;
        this.closeModuleDropdowns();
      }
    },
    shouldRefreshNotifications() {
      const path = String(this.$route?.path || '').toLowerCase();
      return !['/proyectos'].includes(path);
    },
    recomputeUnread() {
      const lista = this.notifications?.alertasSistema || [];
      const unread = lista.filter(a => !a.isRead).length;
      this.notifications.cantidadAlertas = unread;
      this.unreadCount = unread;
    },
    async refreshNotifications() {
      if (!this.proyectId || !this.shouldRefreshNotifications()) return;
      try {
        const resp = await fetchAlertsCustom(this.proyectId);
        this.notifications = resp && Array.isArray(resp)
          ? prepareAlertsData(resp)
          : { cantidadAlertas: 0, alertasSistema: [] };
        sessionStorage.setItem('notifications', JSON.stringify(this.notifications));
        this.recomputeUnread();
      } catch (e) {
        console.warn('No se pudo refrescar alertas:', e);
      }
    },
    async markReadOnServer(ids) {
      for (const id of ids) {
        await fetchWithToken(`/api/update-status-alert-read?id=${id}`, {
          method: 'PUT'
        });
      }
    },
    async markOneRead(alerta) {
      if (!alerta || alerta.isRead || alerta.idAlerts == null) return;
      alerta.isRead = true;
      this.markedReadThisSession.add(alerta.idAlerts);
      this.recomputeUnread();
      try {
        await this.markReadOnServer([alerta.idAlerts]);
      } catch (e) {
        console.warn('No se pudo marcar la alerta como leída:', e);
      }
    },
    async markAllRead() {
      const lista = this.notifications?.alertasSistema || [];
      const ids = lista.filter(a => a && a.idAlerts != null && !a.isRead).map(a => a.idAlerts);
      if (!ids.length) return;
      lista.forEach(a => { if (ids.includes(a.idAlerts)) a.isRead = true; });
      ids.forEach(id => this.markedReadThisSession.add(id));
      this.recomputeUnread();
      try {
        await this.markReadOnServer(ids);
      } catch (e) {
        console.warn('No se pudieron marcar todas como leídas:', e);
      }
    },
    formatAlertTime(alerta) {
      // La API ahora entrega `fechaIso` (timestamp crudo). Antes sólo mandaba
      // `fecha` y `hora` YA FORMATEADOS con DATE_FORMAT('%d-%m-%Y') y '%H:%i',
      // y acá se imprimían tal cual: el formato chileno se imponía a todos los
      // proyectos y ningún cambio de Formato Regional podía corregirlo.
      // `fecha` es inconsistente según el tipo de dispositivo (a veces ya
      // formateado, a veces ISO), así que se intenta formatear el crudo y sólo
      // si no parsea se cae al texto que mandó la API.
      const crudo = alerta?.fechaIso || alerta?.fecha;
      if (crudo && /\d{4}-\d{2}-\d{2}/.test(String(crudo))) {
        const t = formatDate(crudo, 'dateTime', { fallback: '' });
        if (t) return t;
      }
      // Respaldo para respuestas antiguas (o cacheadas) sin fechaIso.
      const fecha = alerta?.fecha || alerta?.fecha_alerta || '';
      const hora = alerta?.hora || '';
      return `${fecha} ${hora}`.trim();
    },
    formatAlertStation(alerta) {
      return alertaEstacion(alerta);
    },
    formatAlertMessage(alerta) {
      return alertaMensaje(alerta);
    },
    extraerSubModulos(seccion) {
      // Superadmin global: ve TODOS los submódulos del proyecto aunque el
      // backend aún no los marque con accesos:true (acceso inmediato a lo que
      // se crea). El resto de usuarios se filtra por el permiso `accesos`.
      const esSuper = this.authStore.esSuperAdmin;
      // Deduplica por url+nombre: la BD puede traer módulos repetidos con
      // distinto id y no deben verse dobles.
      const vistos = new Set();
      return (seccion?.sub_modulos || [])
        .filter(item => item && (esSuper || item.accesos))
        .filter(item => {
          const clave = `${item.url || ''}|${item.nombre || ''}`;
          if (vistos.has(clave)) return false;
          vistos.add(clave);
          return true;
        })
        // El nombre del ítem lo arma la API (helpers/menu/entityMenu.js) y se
        // cachea en sessionStorage.menu, así que un menú anterior a la
        // corrección del prefijo llegaría acá como "Cuartel Cuartel 3". Se
        // colapsa al pintar para no depender de reiniciar sesión; el menú ya
        // corregido pasa intacto (no hay repetición que colapsar).
        .map(item => ({ ...item, nombre: sinTipoDuplicado(item.nombre) }));
    },
    cargarModulos(menu) {
      if (!menu) return;

      this.cuarteles = this.extraerSubModulos(menu?.cuarteles);
      this.casetas = this.extraerSubModulos(menu?.casetas);
      this.meteorologicas = this.extraerSubModulos(menu?.meteorologicas);
      this.alertas = this.extraerSubModulos(menu?.alertas);
      this.administracion = this.extraerSubModulos(menu?.administracion);

      console.log('Módulos cargados - Proyecto:', sessionStorage.getItem('proyectSelectedName'));
    },
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      if (this.isMobileView && this.isCollapsed) {
        this.showDropdownProyectos = false;
        this.closeModuleDropdowns();
      }
    },
    toggleDropdownNotifications() {
      // Ya no marca todo como leído al abrir: el usuario decide con los botones
      // "marcar como leída" (por ítem) y "marcar todas como leídas".
      this.showDropdownNotifications = !this.showDropdownNotifications;
      if (this.showDropdownNotifications) this.showDropdownCuenta = false;
    },
    toggleDropdownCuenta() {
      this.showDropdownCuenta = !this.showDropdownCuenta;
      // Ambos paneles cuelgan de la misma barra: solo uno abierto a la vez.
      if (this.showDropdownCuenta) this.showDropdownNotifications = false;
    },
    toggleDropdownProyectos() {
      this.showDropdownProyectos = !this.showDropdownProyectos;
    },
    seleccionarProyecto(index) {
      this.showDropdownProyectos = false;
      // El store persiste todo y recarga en /home para reinicializar la app
      this.proyectoStore.seleccionar(index, { redirect: true });
    },
    logout() {
      this.showDropdownCuenta = false;
      this.proyectoStore.clear();
      this.authStore.logout();
    },
    closeModuleDropdowns() {
      this.showDropdownCuarteles = false;
      this.showDropdownCasetas = false;
      this.showDropdownMeteo = false;
      this.showDropdownAlertas = false;
      this.showDropdownAdmin = false;
    },
    loadMenu(menu) {
      if (!menu) {
        console.warn('No se proporciono menu para cargar.');
        return;
      }
      this.proyectoStore.hydrate();
      this.perfilSession = getPerfilSession();
      this.userType = getUserTypeFromProyectos(this.proyectos, this.proyectId) ?? getUserTypeFallbackFromToken();
      this.cargarModulos(menu);
    }
  }
};
</script>

<style scoped>
#app {
  display: flex;
  flex-direction: column;
  /* 100dvh (viewport DINAMICO) con respaldo 100vh: en celulares 100vh incluye
     el alto tapado por la barra del navegador, dejando una franja de la app
     fuera de pantalla. Al tocar un boton cercano al borde inferior (p. ej.
     "Expandir leyenda" en el mapa), iOS Safari y Chrome Android hacian scroll
     automatico hacia el elemento enfocado y la barra superior desaparecia.
     Con dvh la app mide exactamente el viewport visible y no hay nada que
     scrollear. */
  height: 100vh;
  height: 100dvh;
}

/* Padding de página único (capa externa). Las vistas anidadas van sin padding. */
.content {
  padding: 12px 16px;
}
.content--map {
  padding: 0;
}

.hb-topbar {
  background: var(--hb-surface);
  padding: 0.7rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 52px;
  border-bottom: 1px solid var(--hb-border);
}

.navbar-left,
.navbar-right {
  display: flex;
  align-items: center;
}

.navbar-right {
  gap: 0.6rem;
}

.header-icon-btn {
  border: 0;
  background: transparent;
  color: var(--hb-text-muted);
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--hb-radius-sm);
  padding: 0;
  cursor: pointer;
}

.header-icon-btn:hover {
  background: var(--hb-surface-2);
  color: var(--hb-text);
}

.header-icon-btn i {
  font-size: 1.02rem;
}

.navbar-brand {
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--hb-accent-strong);
  background: var(--hb-accent-soft);
  padding: 5px 12px;
  border-radius: 20px;
  letter-spacing: 0.1px;
}

.navbar-brand--header {
  margin: 0;
  white-space: nowrap;
}

/* ---- Menú de sesión colgado del nombre del proyecto ---- */
/* El botón hereda .navbar-brand: mismo pill que ya tenía el proyecto */
.account-menu-wrap {
  position: relative;
  display: inline-flex;
}

.account-menu-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 0;
  margin: 0;
  cursor: pointer;
  max-width: 220px;
  transition: filter 0.15s ease;
}

.account-menu-btn:hover,
.account-menu-btn.active {
  filter: brightness(0.96);
}

.account-menu-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-menu-caret {
  font-size: 9px;
  flex: 0 0 auto;
  opacity: 0.75;
}

.account-menu {
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  min-width: 160px;
  max-width: 92vw;
  padding: 0.3rem;
  background: var(--hb-surface);
  border: 1px solid var(--hb-border);
  border-radius: var(--hb-radius);
  box-shadow: 0 10px 28px rgba(16, 40, 30, 0.14);
  z-index: 1000;
  color: var(--hb-text);
}

.account-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
  border: none;
  background: none;
  text-align: left;
  text-decoration: none;
  color: var(--hb-text-muted);
  font-size: 0.82rem;
  font-weight: 500;
  border-radius: var(--hb-radius-sm);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.account-menu-item:hover {
  background: var(--hb-surface-2);
  color: var(--hb-text);
}

.account-menu-item i {
  font-size: 14px;
  width: 16px;
  text-align: center;
}

.account-menu-item--danger:hover {
  color: var(--hb-danger, #c0392b);
}

/* Separador para cuando se agreguen más opciones al menú */
.account-menu-sep {
  height: 1px;
  margin: 0.3rem 0.2rem;
  background: var(--hb-border);
}

.admin-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
}

.sidebar {
  width: 234px;
  background: var(--hb-surface);
  border-right: 1px solid var(--hb-border);
  padding: 0.75rem 0.6rem;
  overflow-y: auto;
  transition: width 0.3s ease, transform 0.28s ease;
  z-index: 10;
}

.sidebar.collapsed {
  width: 60px;
  padding: 0.65rem 0.4rem;
}

.nav-link {
  color: var(--hb-text-muted);
  padding: 0.62rem 0.7rem;
  margin: 1px 0;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  font-size: 0.84rem;
  font-weight: 500;
  letter-spacing: 0.1px;
  line-height: 1.2;
  border-radius: var(--hb-radius-sm);
  transition: background-color 0.15s ease, color 0.15s ease;
}

.nav-link:hover {
  color: var(--hb-text);
  background-color: var(--hb-surface-2);
}

.nav-link.router-link-active {
  color: var(--hb-accent-strong);
  background-color: var(--hb-accent-soft);
  font-weight: 500;
}

.nav-link span {
  white-space: nowrap;
}

.nav-link i {
  font-size: 15px;
  width: 16px;
  text-align: center;
}

.nav-item.dropdown {
  position: relative;
}

.nav-item.dropdown .nav-link {
  padding: 0.75rem 1.5rem;
}

.dropdown-menu {
  display: none;
  position: static;
  float: none;
  width: 100%;
  margin: 2px 0;
  padding: 0.25rem 0;
  border: none;
  box-shadow: none;
  background-color: var(--hb-surface-2);
  border-radius: var(--hb-radius-sm);
}

.sidebar.collapsed .nav-link,
.sidebar.collapsed .nav-item.dropdown .nav-link {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
  gap: 0;
  border-left-width: 0;
}

.sidebar.collapsed .nav-link span,
.sidebar.collapsed .nav-link .fa-chevron-down,
.sidebar.collapsed .sidebar-footer,
.sidebar.collapsed .dropdown-menu {
  display: none !important;
}

.sidebar.collapsed .nav-link i {
  margin: 0;
  font-size: 15px;
}

.dropdown-menu.show {
  display: block;
}

.sidebar-footer {
  padding: 0.9rem 0.9rem 0.4rem;
  border-top: 1px solid var(--hb-border);
  color: var(--hb-text-muted);
  font-size: 0.76rem;
  letter-spacing: 0.1px;
}

.sidebar-footer__brand {
  font-weight: 500;
  color: var(--hb-text);
}

.sidebar-footer__version {
  margin-top: 0.15rem;
  font-size: 0.7rem;
  color: var(--hb-text-muted);
  text-align: center;
}

.dropdown-item {
  color: var(--hb-text-muted);
  padding: 0.45rem 1rem 0.45rem 2.1rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  border: none;
  background: none;
  font-size: 0.78rem;
  font-weight: 400;
  letter-spacing: 0.1px;
  line-height: 1.25;
  border-radius: var(--hb-radius-sm);
}

.dropdown-item:hover {
  color: var(--hb-text);
  background-color: var(--hb-surface);
}

.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}


.content {
  flex: 1;
  /* min-width 0: como item flex, su minimo por defecto es "auto" (el ancho del
     contenido), asi que una tabla o grafico unos pixeles mas ancho que el
     viewport empujaba TODO el layout hacia el lado (el scroll horizontal del
     body que se sentia al arrastrar con el mouse). Con 0, el contenido ancho
     se recorta dentro de .content y el layout nunca crece. */
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2rem;
}

.content.content--map {
  padding: 0;
  background: transparent;
  border: 0;
  outline: 0;
  box-shadow: none;
}

.notif-menu {
  position: absolute;
  right: 0;
  top: 46px;
  width: 340px;
  max-width: 92vw;
  background: var(--hb-surface);
  border: 1px solid var(--hb-border);
  border-radius: var(--hb-radius);
  box-shadow: 0 10px 28px rgba(16, 40, 30, 0.14);
  z-index: 1000;
  color: var(--hb-text);
  overflow: hidden;
}

.notif-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 0.9rem;
  background: var(--hb-surface-2);
  border-bottom: 1px solid var(--hb-border);
}

.notif-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--hb-text);
}

.notif-mark-all {
  border: none;
  background: transparent;
  color: var(--hb-accent, #0a7d5a);
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.notif-mark-all:disabled {
  color: var(--hb-text-muted);
  cursor: default;
  opacity: 0.7;
}

.notif-body {
  max-height: 340px;
  overflow-y: auto;
}

.notif-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.65rem 0.9rem;
  border-top: 1px solid var(--hb-border);
  background: var(--hb-surface, #fff);
  color: var(--hb-accent);
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease;
}

.notif-footer:hover {
  background: var(--hb-accent-soft);
}

.notif-footer i {
  font-size: 0.72rem;
}

.notif-item {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.6rem 0.9rem 0.7rem;
  border-bottom: 1px solid var(--hb-border);
}

.notif-item:last-child {
  border-bottom: none;
}

.notif-item.unread {
  background: var(--hb-accent-soft);
}

.notif-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 0.35rem;
  flex: 0 0 auto;
  background: transparent;
}

.notif-dot.on {
  background: var(--hb-accent, #0a7d5a);
}

.notif-content {
  flex: 1 1 auto;
  min-width: 0;
}

.notif-row-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
}

.notif-station {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--hb-text);
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notif-time {
  font-size: 0.7rem;
  color: var(--hb-text-muted);
  flex: 0 0 auto;
  white-space: nowrap;
}

.notif-text {
  font-size: 0.74rem;
  color: var(--hb-text-muted);
  margin-top: 0.25rem;
  line-height: 1.3;
}

.notif-read-btn {
  border: 1px solid var(--hb-border);
  background: var(--hb-surface);
  color: var(--hb-text-muted);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.notif-read-btn:hover {
  color: #fff;
  background: var(--hb-accent, #0a7d5a);
  border-color: var(--hb-accent, #0a7d5a);
}

.notif-empty {
  padding: 1.4rem 0.8rem;
  font-size: 0.8rem;
  color: var(--hb-text-muted);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
}

.notif-empty i {
  font-size: 1.4rem;
  opacity: 0.5;
}

.notif-body::-webkit-scrollbar {
  width: 6px;
}

.notif-body::-webkit-scrollbar-track {
  background: var(--hb-surface-2);
}

.notif-body::-webkit-scrollbar-thumb {
  background: var(--hb-border-strong);
  border-radius: 8px;
}

.sidebar-backdrop {
  display: none;
}

@media (max-width: 768px) {
  #app {
    --mobile-header-height: 50px;
  }

  .navbar {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    padding: 0.55rem 0.8rem;
    z-index: 1200;
  }

  /* Antes se ocultaba el nombre del proyecto en móvil; ahora es el disparador
     del menú de sesión, así que se mantiene visible pero truncado. */
  .account-menu-btn {
    max-width: 42vw;
    padding: 5px 10px;
  }


  .admin-layout {
    padding-top: var(--mobile-header-height);
  }

  .sidebar {
    position: fixed;
    top: var(--mobile-header-height);
    bottom: 0;
    left: 0;
    width: min(280px, calc(100vw - 44px));
    max-width: calc(100vw - 24px);
    height: auto;
    padding: 0.9rem 0 0.75rem;
    transform: translateX(0);
    z-index: 1100;
    box-shadow: 8px 0 24px rgba(0, 0, 0, 0.26);
  }

  .sidebar.collapsed {
    left: 0;
    width: min(280px, calc(100vw - 44px));
    max-width: calc(100vw - 24px);
    transform: translateX(-110%);
  }

  .sidebar-backdrop {
    display: block;
    position: fixed;
    top: var(--mobile-header-height);
    right: 0;
    bottom: 0;
    left: 0;
    border: 0;
    margin: 0;
    padding: 0;
    background: rgba(15, 23, 42, 0.26);
    z-index: 1090;
  }

  .content {
    padding: 1rem;
  }
}
</style>
